import { useState, useMemo, useCallback, useEffect } from 'react'
import {
  Title,
  Stack,
  Grid,
  Card,
  Text,
  Group,
  Button,
  TextInput,
  Select,
  Badge,
  Loader,
  Center,
  Alert,
  Tooltip,
  Chip,
  Paper,
  Box,
} from '@mantine/core'
import {
  IconArrowsExchange,
  IconSearch,
  IconAlertTriangle,
  IconCheck,
  IconX,
  IconSettings,
  IconChevronDown,
  IconChevronUp,
} from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { getOffensesWithRouting } from '@/services/analytics.api'
import { useVersionContext } from '@/context/VersionContext'
import { usePermissions } from '@/hooks/usePermissions'

interface SearchPanelState {
  searchId: string
  selectedVersion: string | null
  loadedSearchId: string | null
  loadedVersion: string | null
}

const INITIAL_PANEL: SearchPanelState = {
  searchId: '',
  selectedVersion: null,
  loadedSearchId: null,
  loadedVersion: null,
}

interface OffenseRow {
  offense_id?: string
  case_number?: string
  charge?: string
  charge_description?: string
  charge_norm?: string
  disposition?: string
  disp_class?: string
  disposition_norm?: string
  offense_type?: string
  type_class?: string
  ce_decision?: string
  ground_truth?: string
  rules_applied?: string
  rationale?: string
  identity_level?: string
  reportability?: string
  queue?: string
  segment?: string
  match_status?: string
  crime_category?: string
  court_type?: string
  adjudication_category?: string
  all_notes?: string
  charge_date?: string
  disposition_date?: string
  offense_age?: number
  jurisdiction_state?: string
  [key: string]: any
}

interface FieldDef {
  key: string
  label: string
  isDecision?: boolean
  isGroundTruth?: boolean
  isCompareKey?: boolean
  isMatchStatus?: boolean
  internalOnly?: boolean
}

const ALL_FIELDS: FieldDef[] = [
  { key: 'offense_id', label: 'Offense ID' },
  { key: 'case_number', label: 'Case #' },
  { key: 'charge', label: 'Charge', isCompareKey: true },
  { key: 'charge_description', label: 'Charge Description' },
  { key: 'charge_norm', label: 'Charge (Normalized)', internalOnly: true },
  { key: 'disposition', label: 'Disposition', isCompareKey: true },
  { key: 'disp_class', label: 'Disposition Class', internalOnly: true },
  { key: 'disposition_norm', label: 'Disposition (Normalized)', internalOnly: true },
  { key: 'offense_type', label: 'Offense Type' },
  { key: 'type_class', label: 'Type Class', internalOnly: true },
  { key: 'identity_level', label: 'Identity Level' },
  { key: 'reportability', label: 'Reportability' },
  { key: 'queue', label: 'Queue' },
  { key: 'segment', label: 'Segment', internalOnly: true },
  { key: 'match_status', label: 'Match Status', isMatchStatus: true },
  { key: 'crime_category', label: 'Crime Category', internalOnly: true },
  { key: 'court_type', label: 'Court Type' },
  { key: 'adjudication_category', label: 'Adjudication', internalOnly: true },
  { key: 'charge_date', label: 'Charge Date' },
  { key: 'disposition_date', label: 'Disposition Date' },
  { key: 'offense_age', label: 'Offense Age', internalOnly: true },
]

const DEFAULT_VISIBLE = [
  'offense_id',
  'case_number',
  'charge',
  'charge_norm',
  'disposition',
  'disp_class',
  'disposition_norm',
  'offense_type',
  'type_class',
  'queue',
  'match_status',
  'crime_category',
  'court_type',
  'adjudication_category',
  'charge_date',
  'disposition_date',
  'offense_age',
]

function normalizeValue(val: any): string {
  if (val == null || val === '') return ''
  return String(val).trim().toLowerCase()
}

function valuesMatch(a: any, b: any): boolean {
  return normalizeValue(a) === normalizeValue(b)
}

function DecisionBadge({ value }: { value?: string }) {
  if (!value) return <Text size="xs" c="dimmed">—</Text>
  return (
    <Badge
      size="xs"
      color={
        value === 'REPORTABLE' ? 'red'
        : value === 'NOT_REPORTABLE' ? 'green'
        : 'gray'
      }
      variant="light"
    >
      {value}
    </Badge>
  )
}

function GroundTruthBadge({ value }: { value?: string }) {
  if (!value) return <Text size="xs" c="dimmed">—</Text>
  return (
    <Badge
      size="xs"
      color={
        value === 'REPORTABLE' ? 'red'
        : value === 'NOT_REPORTABLE' ? 'green'
        : 'blue'
      }
      variant="outline"
    >
      {value}
    </Badge>
  )
}

function MatchStatusBadge({ value }: { value?: string }) {
  if (!value) return <Text size="xs" c="dimmed">—</Text>
  const upper = value.toUpperCase()
  const color = upper === 'MATCH' ? 'green'
    : upper === 'MISMATCH' ? 'red'
    : upper === 'REQUIRES_REVIEW' ? 'yellow'
    : 'gray'
  const icon = upper === 'MATCH' ? <IconCheck size={10} />
    : upper === 'MISMATCH' ? <IconX size={10} />
    : null
  return (
    <Badge size="xs" color={color} variant="light" leftSection={icon}>
      {value}
    </Badge>
  )
}

function formatValue(val: any): string {
  if (val == null || val === '') return '—'
  if (typeof val === 'object') {
    if (val.value != null) return String(val.value)
    if (val instanceof Date) return val.toISOString().split('T')[0]
    try { return JSON.stringify(val) } catch { return '—' }
  }
  return String(val)
}

function FieldValue({ field, value }: { field: FieldDef; value: any }) {
  if (field.isDecision) return <DecisionBadge value={value} />
  if (field.isGroundTruth) return <GroundTruthBadge value={value} />
  if (field.isMatchStatus) return <MatchStatusBadge value={value} />

  const display = formatValue(value)
  const isLong = display.length > 60

  const content = (
    <Text size="xs" ff={field.key === 'offense_id' ? 'monospace' : undefined} c={display === '—' ? 'dimmed' : undefined}>
      {display}
    </Text>
  )

  return isLong ? (
    <Tooltip label={display} multiline maw={400}>
      {content}
    </Tooltip>
  ) : content
}

function OffenseCard({
  offense,
  other,
  index,
  visibleFields,
}: {
  offense: OffenseRow | null
  other: OffenseRow | null
  index: number
  visibleFields: FieldDef[]
  side: 'left' | 'right'
}) {
  if (!offense) {
    return (
      <Paper p="sm" radius="sm" bg="gray.0" withBorder style={{ borderStyle: 'dashed', height: '100%' }}>
        <Text size="xs" c="dimmed" fs="italic" ta="center">
          #{index + 1} — No matching offense on this side
        </Text>
      </Paper>
    )
  }

  const bothPresent = offense != null && other != null
  const mismatchedKeys = new Set<string>()
  if (bothPresent) {
    for (const f of ALL_FIELDS) {
      if (f.isCompareKey && !valuesMatch(offense[f.key], other[f.key])) {
        mismatchedKeys.add(f.key)
      }
    }
  }
  const hasSideMismatch = mismatchedKeys.size > 0
  const hasDecisionMismatch = offense.match_status?.toUpperCase() === 'MISMATCH'

  return (
    <Paper
      p="xs"
      radius="sm"
      withBorder
      style={{
        height: '100%',
        borderColor: hasSideMismatch ? 'var(--mantine-color-red-4)'
          : hasDecisionMismatch ? 'var(--mantine-color-orange-4)'
          : undefined,
        borderLeftWidth: 3,
        borderLeftColor: hasSideMismatch
          ? 'var(--mantine-color-red-5)'
          : hasDecisionMismatch
          ? 'var(--mantine-color-orange-5)'
          : bothPresent
          ? 'var(--mantine-color-green-5)'
          : 'var(--mantine-color-gray-4)',
        backgroundColor: hasDecisionMismatch ? 'var(--mantine-color-orange-0)' : undefined,
      }}
    >
      <Stack gap={4}>
        <Group justify="space-between" gap={4}>
          <Text size="xs" fw={700} c="dimmed">#{index + 1}</Text>
          <Group gap={4}>
            {hasDecisionMismatch && (
              <Badge size="xs" color="orange" variant="filled" leftSection={<IconAlertTriangle size={8} />}>
                AI ≠ UBS
              </Badge>
            )}
            {bothPresent && (
              hasSideMismatch ? (
                <Badge size="xs" color="red" variant="filled" leftSection={<IconX size={8} />}>
                  {mismatchedKeys.size} diff
                </Badge>
              ) : (
                <Badge size="xs" color="green" variant="filled" leftSection={<IconCheck size={8} />}>
                  OK
                </Badge>
              )
            )}
          </Group>
        </Group>

        {visibleFields.map(field => {
          const isSideMismatched = mismatchedKeys.has(field.key)
          const isDecisionField = field.isMatchStatus && hasDecisionMismatch
          return (
            <Group
              key={field.key}
              gap={4}
              wrap="nowrap"
              align="flex-start"
              py={2}
              px={4}
              style={{
                backgroundColor: isSideMismatched ? 'var(--mantine-color-orange-1)'
                  : isDecisionField ? 'var(--mantine-color-red-1)'
                  : undefined,
                borderRadius: 4,
              }}
            >
              <Text size="xs" fw={600} c="dimmed" style={{ minWidth: 90, flexShrink: 0 }}>
                {field.label}
              </Text>
              <Box style={{ flex: 1, minWidth: 0 }}>
                <FieldValue field={field} value={offense[field.key]} />
              </Box>
              {isSideMismatched && (
                <Tooltip label={`Other side: ${other?.[field.key] || '(empty)'}`}>
                  <Badge size="xs" color="orange" variant="light" style={{ flexShrink: 0 }}>
                    ≠
                  </Badge>
                </Tooltip>
              )}
            </Group>
          )
        })}

        <Grid gutter="xs" mt={4}>
          <Grid.Col span={6}>
            <Paper p="xs" radius="sm" bg="indigo.0" style={{ border: '1px solid var(--mantine-color-indigo-2)' }}>
              <Text size="xs" fw={700} c="indigo.7" mb={4}>AI (Compliance Engine)</Text>
              <Group gap={4} mb={4}>
                <Text size="xs" c="dimmed">Decision:</Text>
                <DecisionBadge value={offense.ce_decision} />
              </Group>
              <Box mb={4}>
                <Text size="xs" c="dimmed">Rules Applied</Text>
                <Text size="xs" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{offense.rules_applied || 'N/A'}</Text>
              </Box>
              <Box>
                <Text size="xs" c="dimmed">Rationale</Text>
                <Text size="xs" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{offense.rationale || 'N/A'}</Text>
              </Box>
            </Paper>
          </Grid.Col>
          <Grid.Col span={6}>
            <Paper p="xs" radius="sm" bg="teal.0" style={{ border: '1px solid var(--mantine-color-teal-2)' }}>
              <Text size="xs" fw={700} c="teal.7" mb={4}>UBS (Ground Truth)</Text>
              <Group gap={4} mb={4}>
                <Text size="xs" c="dimmed">Decision:</Text>
                <GroundTruthBadge value={offense.ground_truth} />
              </Group>
              <Box>
                <Text size="xs" c="dimmed">Notes</Text>
                <Text size="xs" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{offense.all_notes || 'N/A'}</Text>
              </Box>
            </Paper>
          </Grid.Col>
        </Grid>
      </Stack>
    </Paper>
  )
}

function InputPanel({
  label,
  color,
  panel,
  setPanel,
  versionOptions,
  offenseCount,
  isLoading,
  error,
  hideVersion,
}: {
  label: string
  color: string
  panel: SearchPanelState
  setPanel: (updater: (prev: SearchPanelState) => SearchPanelState) => void
  versionOptions: { value: string; label: string }[]
  offenseCount: number | null
  isLoading: boolean
  error: Error | null
  hideVersion?: boolean
}) {
  const handleLoad = useCallback(() => {
    const trimmed = panel.searchId.trim()
    if (!trimmed) return
    setPanel(prev => ({
      ...prev,
      loadedSearchId: trimmed,
      loadedVersion: prev.selectedVersion,
    }))
  }, [panel.searchId, panel.selectedVersion, setPanel])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleLoad()
  }

  return (
    <Card shadow="sm" padding="md" radius="md" withBorder>
      <Stack gap="sm">
        <Group gap="xs">
          <Badge size="md" color={color} variant="filled">{label}</Badge>
          {panel.loadedSearchId && (
            <Text size="xs" c="dimmed">
              {panel.loadedSearchId}
              {!hideVersion && panel.loadedVersion ? ` @ ${panel.loadedVersion}` : ''}
            </Text>
          )}
          {offenseCount !== null && (
            <Badge size="sm" variant="light" color={color}>{offenseCount} offense(s)</Badge>
          )}
          {isLoading && <Loader size="xs" />}
        </Group>
        <Group gap="xs" align="end">
          <TextInput
            placeholder="Search ID"
            value={panel.searchId}
            onChange={e => setPanel(prev => ({ ...prev, searchId: e.target.value }))}
            onKeyDown={handleKeyDown}
            style={{ flex: 1 }}
            size="xs"
          />
          {!hideVersion && (
            <Select
              placeholder="Version"
              data={versionOptions}
              value={panel.selectedVersion}
              onChange={v => setPanel(prev => ({ ...prev, selectedVersion: v }))}
              clearable
              size="xs"
              style={{ width: 160 }}
            />
          )}
          <Button
            onClick={handleLoad}
            disabled={!panel.searchId.trim() || isLoading}
            leftSection={<IconSearch size={14} />}
            size="xs"
            color={color}
          >
            Load
          </Button>
        </Group>
        {error && (
          <Alert color="orange" icon={<IconAlertTriangle size={14} />} p="xs">
            <Text size="xs">
              {error.message?.includes('500')
                ? 'Server error — BigQuery may not be reachable.'
                : error.message || 'Failed to load offenses'}
            </Text>
          </Alert>
        )}
      </Stack>
    </Card>
  )
}

export function CompareSearchesPage() {
  const { versions } = useVersionContext()
  const { isExternalUser, isExpert } = usePermissions()

  const hideVersion = isExternalUser || isExpert

  const [leftPanel, setLeftPanel] = useState<SearchPanelState>(INITIAL_PANEL)
  const [rightPanel, setRightPanel] = useState<SearchPanelState>(INITIAL_PANEL)
  const [visibleFieldKeys, setVisibleFieldKeys] = useState<string[]>(DEFAULT_VISIBLE)
  const [showFieldPicker, setShowFieldPicker] = useState(false)

  const latestVersion = useMemo(
    () => versions.find(v => v.isCurrent)?.version || versions[0]?.version || null,
    [versions],
  )

  useEffect(() => {
    if (!hideVersion && latestVersion) {
      setLeftPanel(prev => prev.selectedVersion === null ? { ...prev, selectedVersion: latestVersion } : prev)
      setRightPanel(prev => prev.selectedVersion === null ? { ...prev, selectedVersion: latestVersion } : prev)
    }
  }, [hideVersion, latestVersion])

  const availableFields = useMemo(
    () => isExternalUser ? ALL_FIELDS.filter(f => !f.internalOnly) : ALL_FIELDS,
    [isExternalUser],
  )

  const visibleFields = useMemo(
    () => availableFields.filter(f => visibleFieldKeys.includes(f.key)),
    [visibleFieldKeys, availableFields],
  )

  const versionOptions = useMemo(() => {
    return versions.map(v => ({
      value: v.version,
      label: v.isCurrent ? `${v.version} (current)` : v.version,
    }))
  }, [versions])

  const leftQuery = useQuery({
    queryKey: ['compare-offenses', 'left', leftPanel.loadedSearchId, leftPanel.loadedVersion],
    queryFn: () => getOffensesWithRouting(leftPanel.loadedSearchId!, leftPanel.loadedVersion || undefined),
    enabled: !!leftPanel.loadedSearchId,
    retry: 1,
    staleTime: 5 * 60 * 1000,
  })

  const rightQuery = useQuery({
    queryKey: ['compare-offenses', 'right', rightPanel.loadedSearchId, rightPanel.loadedVersion],
    queryFn: () => getOffensesWithRouting(rightPanel.loadedSearchId!, rightPanel.loadedVersion || undefined),
    enabled: !!rightPanel.loadedSearchId,
    retry: 1,
    staleTime: 5 * 60 * 1000,
  })

  const leftOffenses: OffenseRow[] = (leftQuery.data as any)?.rows || []
  const rightOffenses: OffenseRow[] = (rightQuery.data as any)?.rows || []

  const pairedOffenses = useMemo(() => {
    if (leftOffenses.length === 0 && rightOffenses.length === 0) return null

    const maxLen = Math.max(leftOffenses.length, rightOffenses.length)
    const pairs: { left: OffenseRow | null; right: OffenseRow | null }[] = []

    for (let i = 0; i < maxLen; i++) {
      pairs.push({
        left: leftOffenses[i] || null,
        right: rightOffenses[i] || null,
      })
    }

    return pairs
  }, [leftOffenses, rightOffenses])

  const summaryStats = useMemo(() => {
    if (!pairedOffenses) return null

    let matches = 0
    let chargeMismatches = 0
    let dispMismatches = 0
    let onlyLeft = 0
    let onlyRight = 0

    for (const pair of pairedOffenses) {
      if (!pair.left && pair.right) { onlyRight++; continue }
      if (pair.left && !pair.right) { onlyLeft++; continue }
      if (!pair.left || !pair.right) continue

      const chargeMatch = valuesMatch(pair.left.charge, pair.right.charge)
      const dispMatch = valuesMatch(pair.left.disposition, pair.right.disposition)

      if (!chargeMatch) chargeMismatches++
      if (!dispMatch) dispMismatches++
      if (chargeMatch && dispMatch) matches++
    }

    return { matches, chargeMismatches, dispMismatches, onlyLeft, onlyRight, total: pairedOffenses.length }
  }, [pairedOffenses])

  const bothLoaded = leftPanel.loadedSearchId && rightPanel.loadedSearchId
    && !leftQuery.isLoading && !rightQuery.isLoading
    && leftOffenses.length > 0 && rightOffenses.length > 0

  return (
    <Stack gap="md" p="md">
      <Group justify="space-between" align="center">
        <Group gap="sm">
          <IconArrowsExchange size={28} />
          <Title order={2}>Compare Searches</Title>
        </Group>
        <Group gap="xs">
          {bothLoaded && summaryStats && (
            <>
              <Badge size="lg" color="green" variant="light">
                {summaryStats.matches} matched
              </Badge>
              {summaryStats.chargeMismatches > 0 && (
                <Badge size="lg" color="red" variant="light">
                  {summaryStats.chargeMismatches} charge diff
                </Badge>
              )}
              {summaryStats.dispMismatches > 0 && (
                <Badge size="lg" color="orange" variant="light">
                  {summaryStats.dispMismatches} disp diff
                </Badge>
              )}
              {(summaryStats.onlyLeft > 0 || summaryStats.onlyRight > 0) && (
                <Badge size="lg" color="gray" variant="light">
                  {summaryStats.onlyLeft + summaryStats.onlyRight} unmatched
                </Badge>
              )}
            </>
          )}
          <Button
            variant="light"
            color="gray"
            size="xs"
            leftSection={<IconSettings size={14} />}
            rightSection={showFieldPicker ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
            onClick={() => setShowFieldPicker(v => !v)}
          >
            Fields ({visibleFieldKeys.length})
          </Button>
        </Group>
      </Group>

      {showFieldPicker && (
        <Paper p="sm" radius="md" withBorder bg="gray.0">
          <Group gap="xs" justify="space-between" mb="xs">
            <Text size="sm" fw={600}>Visible Fields</Text>
            <Group gap={4}>
              <Button
                size="compact-xs"
                variant="subtle"
                onClick={() => setVisibleFieldKeys(availableFields.map(f => f.key))}
              >
                All
              </Button>
              <Button
                size="compact-xs"
                variant="subtle"
                onClick={() => setVisibleFieldKeys(DEFAULT_VISIBLE)}
              >
                Reset
              </Button>
              <Button
                size="compact-xs"
                variant="subtle"
                color="red"
                onClick={() => setVisibleFieldKeys([])}
              >
                None
              </Button>
            </Group>
          </Group>
          <Chip.Group multiple value={visibleFieldKeys} onChange={setVisibleFieldKeys}>
            <Group gap={6}>
              {availableFields.map(f => (
                <Chip key={f.key} value={f.key} size="xs" variant="outline">
                  {f.label}
                </Chip>
              ))}
            </Group>
          </Chip.Group>
        </Paper>
      )}

      <Grid gutter="md">
        <Grid.Col span={6}>
          <InputPanel
            label="Side A"
            color="blue"
            panel={leftPanel}
            setPanel={setLeftPanel}
            versionOptions={versionOptions}
            offenseCount={leftOffenses.length > 0 ? leftOffenses.length : null}
            isLoading={leftQuery.isLoading}
            error={leftQuery.error as Error | null}
            hideVersion={hideVersion}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <InputPanel
            label="Side B"
            color="violet"
            panel={rightPanel}
            setPanel={setRightPanel}
            versionOptions={versionOptions}
            offenseCount={rightOffenses.length > 0 ? rightOffenses.length : null}
            isLoading={rightQuery.isLoading}
            error={rightQuery.error as Error | null}
            hideVersion={hideVersion}
          />
        </Grid.Col>
      </Grid>

      {bothLoaded && summaryStats && (summaryStats.onlyLeft > 0 || summaryStats.onlyRight > 0) && (
        <Alert color="yellow" icon={<IconAlertTriangle size={16} />} p="sm">
          Different offense counts ({leftOffenses.length} vs {rightOffenses.length}).
          Matching by positional order — extra offenses shown as unmatched.
        </Alert>
      )}

      {(leftQuery.isLoading || rightQuery.isLoading) && !pairedOffenses && (
        <Center py="xl">
          <Loader size="md" />
        </Center>
      )}

      {pairedOffenses && pairedOffenses.length > 0 && (
        <Stack gap="sm">
          {pairedOffenses.map((pair, idx) => (
            <Grid key={idx} gutter="md" align="stretch">
              <Grid.Col span={6} style={{ display: 'flex' }}>
                <Box style={{ flex: 1 }}>
                  <OffenseCard
                    offense={pair.left}
                    other={pair.right}
                    index={idx}
                    visibleFields={visibleFields}
                    side="left"
                  />
                </Box>
              </Grid.Col>
              <Grid.Col span={6} style={{ display: 'flex' }}>
                <Box style={{ flex: 1 }}>
                  <OffenseCard
                    offense={pair.right}
                    other={pair.left}
                    index={idx}
                    visibleFields={visibleFields}
                    side="right"
                  />
                </Box>
              </Grid.Col>
            </Grid>
          ))}
        </Stack>
      )}
    </Stack>
  )
}
